import React, { createContext, useContext, useMemo, useState } from 'react'
import { errorHandler, init } from '../../services/app.feathers'
import { AppProviderContext, AppProviderProps } from './types'
import { logger } from '../../services/logger'
import { API_ERROR_CODES } from '../../services/error.service'
import { useConfiguration } from '../AppConfigProvider/AppConfigProvider'

const appProviderContext = createContext<AppProviderContext>(
  {} as AppProviderContext
)

const AppProvider: React.FC<AppProviderProps> = ({ children }): JSX.Element => {
  const [authorizationError, setAuthorizationError] = useState(false)
  const { configuration } = useConfiguration()

  const application = init({
    fetch: window.fetch.bind(window),
    root: `https://${configuration.apiUrl}`
  })

  logger.debug('AppProvider')

  /**
   * Global error catcher
   */
  application.hooks({
    before: {
      all: [
        (context) => {
          // Clear authorization error before any call
          setAuthorizationError(false)
          return context
        }
      ]
    },
    error: {
      all: [
        errorHandler,
        async (context) => {
          /**
           * TODO: DATADOG!!
           */
          logger.error(
            `Error in ${context.path} calling ${context.method} method code: ${context.error?.code}`,
            context.error
          )

          // When an API call returns 401 we should redirect
          if (context.error?.code === API_ERROR_CODES.NotAuthenticated)
            setAuthorizationError(true)

          return context
        }
      ]
    }
  })

  /**
   * Helps with unnecessary re-renders
   * ensures any changes to restClient does not
   * fire a re-render to all child components
   */
  const memoedValue = useMemo(() => ({ application }), [])

  return (
    <appProviderContext.Provider
      value={
        {
          application: memoedValue.application,
          authorizationError
        } as AppProviderContext
      }
    >
      {children}
    </appProviderContext.Provider>
  )
}

const useApp = () => useContext(appProviderContext)

export { appProviderContext, useApp }

export default AppProvider
